import React, { FC } from 'react';

import { LucidTheme, mqMin } from '@lucid/theme';
import { kebabCase } from 'lodash';

import Box from '../box/box';
import Container from '../container/container';
import Heading from '../heading/heading';
import Link from '../link/link';
import Text from '../text/text';
import Image from '../image/image';
import { ComponentImage, ComponentLink } from '../../types/generics';

export interface ResourceSectionItem {
  /** The CTA link and link text for the resource content. */
  cta: ComponentLink;
  /** The title for the resource content. */
  header: string;
  /** The icon to be displayed with the resource content. */
  image: ComponentImage;
  /** The description text for the resource content. */
  description: string;
}

export interface ResourceSectionProps {
  /** The section title to be displayed above the items. */
  header?: string;
  /** The list of resource content data to be displayed. */
  items: ResourceSectionItem[];
  /** The section description to be displayed above the items. */
  subheader?: string;
}

const ResourceSection: FC<ResourceSectionProps> = ({ header, items, subheader }) => (
  <Container
    css={(theme: LucidTheme) => ({
      backgroundColor: theme.colors.white,
    })}
  >
    {header && (
      <Heading
        as="h2"
        css={{
          margin: '0 auto',
          width: '100%',
          maxWidth: '580px',
          textAlign: 'center',
          flex: '0 1 100%',
        }}
        my={{ default: 5, mobile: 6 }}
        variant="headline2XL"
      >
        {header}
      </Heading>
    )}
    {subheader && (
      <Text
        css={{
          margin: '0 auto',
          width: '100%',
          maxWidth: '580px',
          textAlign: 'center',
          flex: '0 1 100%',
        }}
        my={{ default: 5, mobile: 6 }}
        variant="paragraphS300"
      >
        {subheader}
      </Text>
    )}
    <Box
      css={{
        display: 'flex',
        flexFlow: 'column wrap',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        [mqMin.tablet]: {
          flexFlow: 'row wrap',
        },
      }}
    >
      {items.map((item, index) => {
        const { cta, header, image, description } = item;

        return (
          <Box
            key={`${header}-${index}`}
            css={(theme: LucidTheme) => ({
              flex: '0 1 100%',
              display: 'flex',
              flexDirection: 'column',
              margin: `${theme.space[5]}px ${theme.space[0]} ${theme.space[4]}px`,
              justifyContent: 'space-between',
              [mqMin.tabletSmall]: {
                flexDirection: 'row',
                margin: `${theme.space[7]}px ${theme.space[0]}`,
              },
              [mqMin.tabletLandscape]: {
                flex: '0 1 48%',
              },
            })}
          >
            <Box
              css={(theme: LucidTheme) => ({
                flex: '0 1 100%',
                alignSelf: 'flex-start',
                maxHeight: '75px',
                marginRight: theme.space[4],
                textAlign: 'right',
                [mqMin.tabletSmall]: {
                  flex: '0 1 160px',
                  alignSelf: 'center',
                  maxWidth: '160px',
                },
              })}
            >
              <Image
                css={(theme: LucidTheme) => ({
                  maxHeight: '50px',
                  marginBottom: theme.space[3],
                  [mqMin.tabletSmall]: {
                    maxHeight: '75px',
                    maxWidth: 'inherit',
                  },
                })}
                imageAlt={image.imageAlt || ''}
                imageSrc={image.imageSrc!}
                loading="lazy"
              />
            </Box>
            <Box
              css={(theme: LucidTheme) => ({
                flex: '0 1 calc(100% - 180px)',
                borderLeft: 0,
                borderTop: `1px solid ${theme.colors.coolGrey[10]}`,
                paddingTop: theme.space[4],
                paddingLeft: theme.space[0],
                [mqMin.tabletSmall]: {
                  borderTop: 0,
                  borderLeft: `1px solid ${theme.colors.coolGrey[10]}`,
                  paddingLeft: theme.space[4],
                },
              })}
            >
              <Heading as="h3" id={kebabCase([header, 'resource-section-heading'])} m={0} variant="headlineL">
                {header}
              </Heading>
              {description && (
                <Box
                  css={(theme: LucidTheme) => ({
                    lineHeight: theme.lineHeights.body,
                    margin: `${theme.space[3]}px ${theme.space[0]}`,
                    'strong, b': {
                      fontWeight: theme.fontWeights.subheading,
                    },
                  })}
                >
                  <Text
                    css={{
                      whiteSpace: 'pre-line',
                    }}
                    id={kebabCase([header, 'resource-section-subheading'])}
                  >
                    {description}
                  </Text>
                </Box>
              )}
              <Link
                hasArrow
                aria-describedby={kebabCase([
                  header,
                  description ? 'resource-section-subheading' : 'resource-section-heading',
                ])}
                to={cta.linkUrl!}
              >
                {cta.linkText}
              </Link>
            </Box>
          </Box>
        );
      })}
    </Box>
  </Container>
);

export default ResourceSection;
